import { AscFile } from '../common-types';
import {
  AscCrosswordPuzzleBoardSize,
  AscCrosswordPuzzleWordCompleted,
  AscCrosswordPuzzleWordSolve
} from './crossword-puzzle-activity';
import {
  AscFindTheWordsPuzzleBoardSize,
  AscFindTheWordsPuzzleWord,
} from './find-the-words-puzzle-activity';
import { AscFlashcardQuestion } from './flashcard-activity';
import { QuizDragAndDropQuestion, QuizQuestion } from './quiz-activity';
import { AscTrueOrFalseQuestion } from './true-or-false-activity';

export interface Activity {
  id: string;
  title?: string;
  completed?: boolean;
  type:
  | 'dragDrop'
  | 'essay'
  | 'video'
  | 'crossword'
  | 'findWord'
  | 'flashcards'
  | 'timeline'
  | 'trueFalse'
  | 'markWord'
  | 'dragWord'
  | 'quiz'
  | 'quizDragDrop'
  | 'assessment';
  activityDescription?: string;
  activityTitle?: string;
  note?: {
    text?: string;
    imageUrl?: string;
    files?: AscFile[];
  };
  journalNote?: {
    id: string;
    body: string;
    updatedAt: string
  };
  attemptsNum?: number;
  attemptsLeft?: number;
}

export interface DragAndDropActivity extends Activity {
  cards: {
    id: number;
    name: string;
  }[];
  groups: {
    id: number;
    name: string;
    imageUrl: string;
  }[];
  correctAnswers?: {
    id: number;
    groupId: number;
    name: string;
  }[];
  response?: {
    name: string;
    answers: number[];
  }[];
}
export interface TimelineActivity extends Activity {
  cards: {
    id: string;
    name: string;
  }[];
  groups: {
    id: string;
    name: string;
    imageUrl: string;
  }[];
  correctAnswers?: {
    id: string;
    groupId: string;
    name: string;
  }[];
  response?: {
    name: string;
    answers: string[];
  }[];
}
export interface DragTheWordActivity extends Activity {
  cards: {
    id: string;
    word: string;
  }[];
  sentences: {
    id: string;
    text: string;
  }[];
  response?: {
    id: string
    answer: string
  }[]
}
export interface EssayActivity extends Activity {
  placeholder?: string;
  response?: {
    date: string;
    essay: string;
  };
};
export interface MarkTheWordActivity extends Activity {
  text: string;
  question: string;
  answers: string[];
  response?: string[];
}
export interface CrosswordActivity extends Activity {
  size: AscCrosswordPuzzleBoardSize;
  wordsAcross: (AscCrosswordPuzzleWordSolve & Partial<Record<'id' | "word", string>>)[];
  wordsDown: (AscCrosswordPuzzleWordSolve & Partial<Record<'id' | "word", string>>)[];
  response?: AscCrosswordPuzzleWordCompleted[];
}
export interface FindTheWordsActivity extends Activity {
  size: AscFindTheWordsPuzzleBoardSize;
  generatedWords: AscFindTheWordsPuzzleWord[];
  response?: {
    id: string
    answer: string
  }[]
}
export interface FlashcardActivity extends Activity {
  questions: AscFlashcardQuestion[];
  response?: {
    id: string
    answer: string
  }[]
}
export interface TrueOrFalseActivity extends Activity {
  questions: AscTrueOrFalseQuestion[];
  response: {
    answer: boolean;
    id: string;
  }[];
}

export interface QuizQuestionActivity extends Activity {
  questions: QuizQuestion[];
  response?: {
    /**
     * Question id
     */
    id: number
    answer: string
  }[]
}
export interface QuizDragAndDropQuestionActivity extends Activity {
  question: QuizDragAndDropQuestion;
  response?: {
    questionId: string
    imageId: string
  }[]
}
export interface VideoActivity extends Activity {
  type: 'video';
  coverImageUrl?: string;
  videoPrompt?: string;
  videoUrl?: string;
  digDeeper?: {
    title?: string;
    description?: string;
    additionalFilesLabel?: string;
    files?: AscFile[];
  };
  reflection?: {
    question?: string;
    numberOfResponses?: number;
    submitButtonText?: string;
  };
  challenge?: {
    title?: string;
    description?: string;
    estimationInHours?: number;
    badgeId: string;
    participatingStudents?: {
      id: string;
      name: string;
      avatarUrl?: string;
    }[];
  };
}

export interface AssessmentActivity extends Activity {
  questions: (QuizQuestion & { answerExplanation?: string })[];
  response?: {
    /**
     * Question id
     */
    id: number
    answer: string
  }[]
  score?: number;
}

export type Activities =
  | Activity
  | VideoActivity
  | DragAndDropActivity
  | EssayActivity
  | VideoActivity
  | FlashcardActivity
  | TimelineActivity
  | TrueOrFalseActivity
  | CrosswordActivity
  | FindTheWordsActivity
  | MarkTheWordActivity
  | DragTheWordActivity
  | QuizQuestionActivity
  | QuizDragAndDropQuestionActivity
  | AssessmentActivity;


export const ActivitiesDictionary = {
  "dragDrop": {} as DragAndDropActivity,
  "essay": {} as EssayActivity,
  "video": {} as VideoActivity,
  "flashcards": {} as FlashcardActivity,
  "timeline": {} as TimelineActivity,
  "trueFalse": {} as TrueOrFalseActivity,
  "crossword": {} as CrosswordActivity,
  "findWord": {} as FindTheWordsActivity,
  "markWord": {} as MarkTheWordActivity,
  "dragWord": {} as DragTheWordActivity,
  "quiz": {} as QuizQuestionActivity,
  "quizDragDrop": {} as QuizDragAndDropQuestionActivity,
  "assessment": {} as AssessmentActivity,
} as const

export type ActivitiesDictionary = typeof ActivitiesDictionary;